import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Sparkles, ArrowRight, MessageSquare, LogIn, Facebook, Twitter, Instagram } from "lucide-react";

const TikTok = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A4.3 4.3 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.77a5.7 5.7 0 1 0 4.91 5.63V9.01a7.3 7.3 0 0 0 4.26 1.36V7.29a4.3 4.3 0 0 1-3.2-1.47Z" />
  </svg>
);

const SOCIALS = [
  { name: "Facebook", href: "https://www.facebook.com/narccheckai", Icon: Facebook },
  { name: "X (Twitter)", href: "https://x.com/narccheckai", Icon: Twitter },
  { name: "TikTok", href: "https://www.tiktok.com/@narccheckai", Icon: TikTok },
  { name: "Instagram", href: "https://www.instagram.com/narccheckai", Icon: Instagram },
];
import { Brand } from "../components/NavBar";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { track } from "../lib/api";

const TACTICS = ["Gaslighting", "DARVO", "Blame-shifting", "Stonewalling", "Love-bombing", "Invalidation"];

const POINTS = [
  "Analyze conversation screenshots",
  "Understand confusing exchanges",
  "Explore possible manipulation patterns",
  "Find practical ways to respond & set boundaries",
];

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { is_pro, loaded } = useEntitlements();

  // Subscribers skip the funnel and go straight to the tool.
  useEffect(() => {
    if (loaded && is_pro) navigate("/app", { replace: true });
  }, [loaded, is_pro, navigate]);

  const getStarted = () => {
    track("onboarding_started");
    navigate("/onboarding");
  };

  return (
    <>
      <header className="nav">
        <div className="container-nc nav-inner">
          <Brand onClick={() => navigate("/")} />
          <nav className="nav-actions">
            <button className="btn btn-ghost" onClick={() => navigate("/sign-in")} data-testid="landing-signin">
              <LogIn size={16} /> <span className="nav-label">Sign in</span>
            </button>
          </nav>
        </div>
      </header>

      <main className="container-nc" style={{ paddingTop: 48, paddingBottom: 90 }}>
        <div className="fade-up" style={{ maxWidth: 680 }}>
          <span className="chip chip-brand" data-testid="landing-badge">
            <Sparkles size={13} /> AI conversation clarity
          </span>
          <h1
            data-testid="landing-title"
            style={{
              fontSize: "clamp(2.6rem, 6.5vw, 4.4rem)",
              lineHeight: 1.03,
              letterSpacing: "-0.04em",
              fontWeight: 600,
              margin: "20px 0 16px",
            }}
          >
            Understand Your <span style={{ color: "var(--brand)" }}>Conversations</span> with NarcCheck AI
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 17.5, lineHeight: 1.6, maxWidth: 580 }}>
            Get clarity on the conversations that leave you second-guessing yourself. NarcCheck AI is a
            narcissist text message analyzer: paste a conversation, drop in screenshots, or record a voice
            note, and it breaks down narcissistic communication patterns, gaslighting text messages, and
            other signs of emotional manipulation in relationships, so confusing exchanges finally make sense.
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 22 }}>
            {TACTICS.map((t) => (
              <span key={t} className="chip chip-neutral">{t}</span>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 30, alignItems: "center" }}>
            <button className="btn btn-primary btn-lg" onClick={getStarted} data-testid="get-started-btn">
              Get Started <ArrowRight size={18} />
            </button>
            <span style={{ color: "var(--muted)", fontSize: 14 }}>
              Already a member?{" "}
              <span className="link" onClick={() => navigate("/sign-in")} data-testid="landing-signin-link">
                Sign in
              </span>
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 20, color: "var(--muted)", fontSize: 13 }}>
            <Lock size={14} color="var(--brand-2)" />
            <span data-testid="landing-privacy">Private by design — your conversations are used only to generate your analysis, never sold.</span>
          </div>
        </div>

        <div className="card card-pad fade-up" style={{ marginTop: 44, maxWidth: 560, animationDelay: "0.08s" }}>
          <div className="eyebrow" style={{ marginBottom: 14 }}>What you'll get</div>
          {POINTS.map((p) => (
            <div key={p} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
              <span style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--brand-tertiary)", display: "grid", placeItems: "center", flexShrink: 0 }}>
                <MessageSquare size={13} color="var(--brand)" />
              </span>
              <span style={{ fontSize: 14.5 }}>{p}</span>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, color: "var(--muted)", fontSize: 12 }}>
            <ShieldCheck size={13} /> For personal reflection only — not professional, medical or legal advice.
          </div>
        </div>
      </main>
      <footer className="container-nc" style={{ borderTop: "1px solid var(--border)", paddingTop: 24, paddingBottom: 40, display: "flex", flexWrap: "wrap", gap: 20, justifyContent: "space-between", alignItems: "center", color: "var(--muted)", fontSize: 13 }} data-testid="landing-footer">
        <span>© {new Date().getFullYear()} NarcCheck AI</span>
        <div style={{ display: "flex", gap: 16 }} data-testid="footer-socials">
          {SOCIALS.map(({ name, href, Icon }) => (
            <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={`NarcCheck AI on ${name}`} title={name}
              style={{ color: "var(--muted)", display: "inline-flex" }} data-testid={`footer-social-${name.split(" ")[0].toLowerCase()}`}>
              <Icon size={18} />
            </a>
          ))}
        </div>
        <nav style={{ display: "flex", gap: 20 }}>
          <a className="link" href="/support" data-testid="footer-support">Support</a>
          <a className="link" href="/privacy" data-testid="footer-privacy">Privacy</a>
          <a className="link" href="/terms" data-testid="footer-terms">Terms</a>
        </nav>
      </footer>
    </>
  );
}
